import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import moment from "moment";

import { t } from '../common/LocalizationProvider';
import {formatNumber, formatHoursSeparated, formatSpeed, formatSpeedSeparated} from "../common/formatter";
import MainToolbar from "../MainToolbar";

import Tooltip from "@material-ui/core/Tooltip";
import {Snackbar, useMediaQuery, useTheme} from "@material-ui/core";
import {
    DirectionsCar as DirectionsCarIcon,
    Warning as WarningIcon,
    Timeline as TimelineIcon,
    PauseCircleFilled as PauseCircleFilledIcon,
    PlayCircleFilled as PlayCircleFilledIcon,
    NotificationsActive as NotificationsActiveIcon,
    FormatListBulleted as FormatListBulletedIcon,
    TrendingUp as TrendingUpIcon,
    EcoSharp,
    GroupWork as GroupWorkIcon,
} from "@material-ui/icons";
import {
    Box, Typography, makeStyles, Grid, Divider,FormControl, InputLabel,
    Select, MenuItem, TextField, Button, Paper, CircularProgress, Container
} from "@material-ui/core";
import {devicesSelectedActions, periodActions, dashboardReportActions} from "../store";
import CssBaseline from "@material-ui/core/CssBaseline";
import SettingsDrawer from "../shared/SettingsDrawer";
import {cyberStyles} from "../cssShared/cyberTheme";
import Alert from "@material-ui/lab/Alert";

const useStyles = makeStyles((theme) => ({
    ...cyberStyles(theme),
    pageHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
    },
    section: {
        marginBottom: theme.spacing(4),
    },
    infoCardIconBox: {
        borderRadius: '10px',
        padding: theme.spacing(1.2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexShrink: 0,
        boxShadow: '0 0 10px rgba(0,0,0,0.3)',
    },
    '@keyframes pulse': {
        '0%': { transform: 'scale(1)', opacity: 0.5 },
        '100%': { transform: 'scale(1.2)', opacity: 0 },
    },
    iconSocilaMedia: {
        borderRadius: '50%',
        width: 25,
        height: 25,
        objectFit: 'contain',
        filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.5))'
    },
}));


const getInfoCardIconStyle = (color) => ({
    backgroundColor: color,
});

const ValueLoader = ({ size = 20 }) => {
    const classes = useStyles();
    return (
        <Box className={classes.valueLoader}>
            <CircularProgress size={size} thickness={4} />
        </Box>
    );
}

const Dashboard = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
    const history = useHistory();
    const isInitialLoad = useRef(true);
    const dispatch = useDispatch();

    const user = useSelector(state => !!state.session.server && state.session.user);
    const devices = useSelector(state => Object.values(state.devices.items));
    const period = useSelector(state => state.period.period)
    const deviceIds = useSelector(state => state.devicesSelected.devicesSelected ?? []);

    const [selectedFrom, setSelectedFrom] = useState(moment().startOf('day'));
    const [selectedTo, setSelectedTo] = useState(moment().endOf('day'));
    const data = useSelector(state => state.dashboardReport.data);
    const [loading, setLoading] = useState(true);
    const [hideDrawer, setHideDrawer] = useState(() => localStorage.getItem('hideDrawer') === 'true');
    const [openSnackbar, setOpenSnackbar] = useState(null)


    const fetchData = async (isCustomTrigger = false) => {
        setLoading(true);
        let from, to;
        try {
            if (isCustomTrigger) {
                if (!selectedFrom || !selectedFrom.isValid() || !selectedTo || !selectedTo.isValid()) {
                    console.error("Invalid custom dates detected during custom fetch trigger.");
                    setLoading(false);
                    return;
                }
                from = selectedFrom;
                to = selectedTo;
            }
            else {
                switch (period) {
                    case 'today':
                        from = moment().startOf('day');
                        to = moment().endOf('day');
                        break;
                    case 'yesterday':
                        from = moment().subtract(1, 'day').startOf('day');
                        to = moment().subtract(1, 'day').endOf('day');
                        break;
                    case 'thisWeek':
                        from = moment().startOf('week');
                        to = moment().endOf('week');
                        break;
                    case 'previousWeek':
                        from = moment().subtract(1, 'week').startOf('week');
                        to = moment().subtract(1, 'week').endOf('week');
                        break;
                    case 'thisMonth':
                        from = moment().startOf('month');
                        to = moment().endOf('month');
                        break;
                    case 'previousMonth':
                        from = moment().subtract(1, 'month').startOf('month');
                        to = moment().subtract(1, 'month').endOf('month');
                        break;
                    case 'custom':
                        if (!selectedFrom || !selectedFrom.isValid() || !selectedTo || !selectedTo.isValid()) {
                            console.error("Invalid custom dates detected in switch case 'custom'.");
                            setLoading(false);
                            return;
                        }
                        from = selectedFrom;
                        to = selectedTo;
                        break;
                    default:
                        from = moment().startOf('day');
                        to = moment().endOf('day');
                        break;
                }
            }
            if (!moment.isMoment(from) || !moment.isMoment(to) || !from.isValid() || !to.isValid()) {
                console.error("Invalid final date range determined before fetch:", from, to);
                setLoading(false);
                dispatch(dashboardReportActions.setData({}));
                return;
            }
            const fromISO = from.toISOString();
            const toISO = to.toISOString();
            const headers = { Accept: 'application/json' };

            const query = new URLSearchParams();
            (deviceIds ?? [0]).forEach(id => query.append("deviceIds", id));
            query.append("from", fromISO);
            query.append("to", toISO);

            const response = await fetch(`/api/reports/dashboard?${query.toString()}`, { headers });
            if (response.ok) {
                const fetchedData = await response.json();
                dispatch(dashboardReportActions.setData(fetchedData));
                if (isCustomTrigger)
                    dispatch(periodActions.update('custom'))
                if (isInitialLoad.current) isInitialLoad.current = false;
            } else {
                console.error("Failed to fetch dashboard data:", response.statusText);
                dispatch(dashboardReportActions.setData({}));
            }
        } catch (e) {
            console.error("Error fetching dashboard data:", e);
            dispatch(dashboardReportActions.setData({}));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (deviceIds.length === 0)
            dispatch(devicesSelectedActions.updateDevicesSelected([0]));
        if (period !== 'custom') {
            fetchData();
        } else if (isInitialLoad.current && selectedFrom && selectedTo) {
            fetchData(true);
        }
    }, [period, deviceIds]);

    const handleCustomFetchClick = () => {
        if (!selectedFrom || !selectedFrom.isValid() || !selectedTo || !selectedTo.isValid() || selectedFrom.isAfter(selectedTo)) {
            console.error("Invalid custom date range.");
            return;
        }
        fetchData(true);
    };

    const handleSelectChange = (event) => {
        let selectedIds = event.target.value.map(Number);

        if (selectedIds.includes(0)) {
            if (selectedIds.length > 1) {
                if (deviceIds.includes(0))
                    dispatch(devicesSelectedActions.updateDevicesSelected(selectedIds.filter(id => id !== 0)));
                else
                    dispatch(devicesSelectedActions.updateDevicesSelected([0]));
            } else {
                dispatch(devicesSelectedActions.updateDevicesSelected([0]));
            }
        } else if (selectedIds.length === 0) {
            dispatch(devicesSelectedActions.updateDevicesSelected([0]));
        } else {
            dispatch(devicesSelectedActions.updateDevicesSelected(selectedIds));
        }
    };

    const handleHistory = (path, type) => {
        if (deviceIds.length === 1 && deviceIds.includes(0)) {
            const allDeviceIds = devices.map(device => device.id);
            dispatch(devicesSelectedActions.updateDevicesSelected(allDeviceIds))
        }
        if (path === '/reports/chart' && deviceIds.length > 1) {
            setOpenSnackbar('selectOneDevice')
            return;
        }
        history.push(type ? `${path}?type=${type}` : path)
        // history.push(`${path}`)
    }

    const renderInfoCards = () => {
        return (
            <Grid container spacing={isSmallScreen ? 2 : 3} className={classes.section}>
                {/* Card 1: Groups */}
                <Grid item xs={6} sm={4} md onClick={() => history.push(`/admin/users`)}>
                    <Paper className={classes.infoCard} elevation={0}>
                        <Box className={classes.infoCardContent}>
                            <Typography className={classes.infoCardLabel}>{t('settingsGroups')}</Typography>
                            <Box className={classes.infoCardValueContainer}>
                                <Typography className={classes.kpiValue}>{data?.numberGroups ?? 0}</Typography>
                            </Box>
                        </Box>
                        <Box className={classes.infoCardIconBox} style={getInfoCardIconStyle('#00d2ff')}>
                            <GroupWorkIcon />
                        </Box>
                    </Paper>
                </Grid>
                {/* Card 2: Total Devices */}
                <Grid item xs={6} sm={4} md onClick={() => {
                    dispatch(devicesSelectedActions.updateDevicesSelected([]));
                    history.push(`/admin/devicesInfo/${user.id}`)
                }}>
                    <Paper className={classes.infoCard} elevation={0}>
                        <Box className={classes.infoCardContent}>
                            <Typography className={classes.infoCardLabel}>{t('TotalDevices')}</Typography>
                            <Box className={classes.infoCardValueContainer}>
                                <Typography className={classes.kpiValue}>{data?.numberDevices ?? 0}</Typography>
                            </Box>
                        </Box>
                        <Box className={classes.infoCardIconBox} style={getInfoCardIconStyle('#00ff88')}>
                            <DirectionsCarIcon />
                        </Box>
                    </Paper>
                </Grid>
                {/* Card 3: Devices Out of Service */}
                <Grid item xs={6} sm={6} md onClick={() => {
                    dispatch(devicesSelectedActions.updateDevicesSelected(data?.devicesOutServices?.length ? data.devicesOutServices : [0]));
                    history.push(`/admin/devicesInfo/${user.id}`)
                }}>
                    <Paper className={classes.infoCard} elevation={0}>
                        <Box className={classes.infoCardContent}>
                            <Typography className={classes.infoCardLabel}>{t('outService')}</Typography>
                            <Box className={classes.infoCardValueContainer}>
                                <Typography className={classes.kpiValue}>{data?.devicesOutServices.length ?? 0}</Typography>
                            </Box>
                        </Box>
                        <Box className={classes.infoCardIconBox} style={getInfoCardIconStyle('#ff4444')}>
                            <WarningIcon />
                        </Box>
                    </Paper>
                </Grid>
                {/* Card 4: Expired Devices*/}
                <Grid item xs={6} sm={4} md onClick={() => {
                    dispatch(devicesSelectedActions.updateDevicesSelected(data?.expiredDevices?.length ? data.expiredDevices : [0]));
                    history.push(`/admin/devicesInfo/${user.id}`)
                }}>
                    <Paper className={classes.infoCard} elevation={0}>
                        <Box className={classes.infoCardContent}>
                            <Typography className={classes.infoCardLabel}>{t('expiredDevices')}</Typography>
                            <Box className={classes.infoCardValueContainer}>
                                <Typography className={classes.kpiValue}>
                                    {data?.expiredDevices.length ?? '0.0'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={classes.infoCardIconBox} style={getInfoCardIconStyle('#ff0055')}>
                            <img className={classes.iconSocilaMedia} src={`images/socialMedia/speedViolation.png`} alt="speed"/>
                        </Box>
                    </Paper>
                </Grid>
                {/* Card 5: Devices Expiring Soon*/}
                <Grid item xs={12} sm={6} md onClick={() => {
                    dispatch(devicesSelectedActions.updateDevicesSelected(data?.expiringDevicesSoon?.length ? data.expiringDevicesSoon : [0]));
                    history.push(`/admin/devicesInfo/${user.id}`)
                }}>
                    <Paper className={classes.infoCard} elevation={0}>
                        <Box className={classes.infoCardContent}>
                            <Typography className={classes.infoCardLabel}>{t('expiringDevicesSoon')}</Typography>
                            <Box className={classes.infoCardValueContainer}>
                                <Typography className={classes.kpiValue}>
                                    {data?.expiringDevicesSoon.length ?? '0.0'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={classes.infoCardIconBox} style={getInfoCardIconStyle('#ffaa00')}>
                            <img className={classes.iconSocilaMedia} src={`images/socialMedia/geofenceViolation.png`} alt="speed"/>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    const renderKpiSelector = () => (
        <Paper className={classes.kpiSelectorContainer} elevation={0}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                {/* Title */}
                <Grid item xs={12} sm={4}>
                    <Tooltip title={
                        `${t('KeyPerformanceIndicators')} ${deviceIds.includes(0) ? ` (${t('allDevices')})` :
                            ` (${deviceIds.length} ${deviceIds.length === 1 ? t('sharedDevice') : t('deviceTitle')})`}`
                    }>
                        <Typography variant="h6" className={classes.kpiSelectorTitle}>
                            {t('KeyPerformanceIndicators')}
                        </Typography>
                    </Tooltip>
                </Grid>

                {/* Device Selector */}
                <Grid item xs={12} sm={4} md={3}>
                    <FormControl variant="filled" fullWidth size="small" disabled={loading} className={classes.formControl}>
                        <InputLabel>{t('sharedDevice')}</InputLabel>
                        <Select
                            multiple
                            value={deviceIds || t('allDevices')}
                            onChange={handleSelectChange}
                            MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                        >
                            <MenuItem key={0} value={0} className={classes.menuItem}>{t('allDevices')}</MenuItem>
                            {devices.map((device) => (
                                <MenuItem key={device.id} value={device.id} className={classes.menuItem}>{device.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Period Selector */}
                <Grid item xs={12} sm={4} md={3}>
                    <FormControl variant="filled" fullWidth size="small" disabled={loading} className={classes.formControl}>
                        <InputLabel>{t('reportPeriod') || 'Period'}</InputLabel>
                        <Select
                            value={period}
                            onChange={(e) => dispatch(periodActions.update(e.target.value))}
                            label={t('reportPeriod') || 'Period'}
                            renderValue={(selectedValue) => (
                                <span>{t(`report${selectedValue.charAt(0).toUpperCase() + selectedValue.slice(1)}`) || selectedValue}</span>
                            )}
                            MenuProps={{
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                getContentAnchorEl: null,
                                PaperProps: { className: classes.periodMenuPaper },
                            }}
                        >
                            <MenuItem value="today" disabled={loading} className={classes.menuItem}>{t('reportToday')}</MenuItem>
                            <MenuItem value="yesterday" disabled={loading} className={classes.menuItem}>{t('reportYesterday')}</MenuItem>
                            <MenuItem value="thisWeek" disabled={loading} className={classes.menuItem}>{t('reportThisWeek')}</MenuItem>
                            <MenuItem value="previousWeek" disabled={loading} className={classes.menuItem}>{t('reportPreviousWeek')}</MenuItem>
                            <MenuItem value="thisMonth" disabled={loading} className={classes.menuItem}>{t('reportThisMonth')}</MenuItem>
                            <MenuItem value="previousMonth" disabled={loading} className={classes.menuItem}>{t('reportPreviousMonth')}</MenuItem>
                            {/* ... other standard MenuItems ... */}

                            <MenuItem value="custom" disableRipple disableGutters divider>
                                <Box display="flex" flexDirection="column" p={2} width="100%" onClick={(e) => e.stopPropagation()}>
                                    <Typography variant="body2" gutterBottom className={classes.kpiSelectorTitle} style={{fontSize: '0.85rem'}}>
                                        {t('reportCustom') || 'Custom Range'}
                                    </Typography>
                                    <TextField
                                        label={t('reportFrom')}
                                        type="datetime-local"
                                        className={classes.customRangeInput}
                                        value={selectedFrom.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)}
                                        onChange={(e) => setSelectedFrom(moment(e.target.value, moment.HTML5_FMT.DATETIME_LOCAL_SECONDS))}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined" size="small" fullWidth margin="dense" disabled={loading}
                                    />
                                    <TextField
                                        label={t('reportTo')}
                                        type="datetime-local"
                                        className={classes.customRangeInput}
                                        value={selectedTo.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            max: selectedFrom.clone().add(31, 'days').format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
                                            min: selectedFrom.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
                                        }}
                                        onChange={(e) => setSelectedTo(moment(e.target.value, moment.HTML5_FMT.DATETIME_LOCAL_SECONDS))}
                                        variant="outlined" size="small" fullWidth margin="dense" disabled={loading}
                                    />
                                    <Box mt={2} display="flex" justifyContent="flex-end">
                                        <Button
                                            size="small" variant="contained"
                                            className={classes.goButton}
                                            disabled={loading || !selectedFrom.isValid() || !selectedTo.isValid()}
                                            onClick={(e) => { e.stopPropagation(); handleCustomFetchClick(); }}
                                        >
                                            {t('reportGo') || 'Go'}
                                        </Button>
                                    </Box>
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Paper>
    );

    const renderKpiCircles = () => {
        return (
            <Grid container spacing={2} className={classes.section}>
                {[
                    { label: t('positionTotalDistance'), val: (data?.totalDistances * 0.001)?.toFixed(0), unit: 'KM', path: '/reports/summary' },
                    { label: t('AverageDailyDistance'), val: (data?.averageDistance * 0.001)?.toFixed(0), unit: 'KM/Day', path: '/reports/chart', type: 'distance' },
                    { label: t('averageSpeed'), val: formatSpeedSeparated(data?.averageSpeed).value, unit: formatSpeedSeparated(data?.averageSpeed).unit, path: '/reports/chart', type: 'speed' },

                    { label: t('TotalEngineRun'), val: formatHoursSeparated(data?.totalEnginesRunTime).value, unit: formatHoursSeparated(data?.totalEnginesRunTime).unit, path: '/reports/trip' },
                    { label: t('TotalEngineStop'), val: formatHoursSeparated(data?.totalEnginesStopTime).value, unit: formatHoursSeparated(data?.totalEnginesStopTime).unit, path: '/reports/stop' },

                    { label: t('AverageDailyRuntime'), val: formatHoursSeparated(data?.averageRunTime).value, unit: `${formatHoursSeparated(data?.averageRunTime).unit}/Day`, path: '/reports/trip' },
                    { label: t('AverageDailyStoptime'), val: formatHoursSeparated(data?.averageStopTime).value, unit: `${formatHoursSeparated(data?.averageStopTime).unit}/Day`, path: '/reports/stop' },

                    { label: t('tripsNumber'), val: data?.numberTrips, path: '/reports/trip' },
                    { label: t('stopsNumber'), val: data?.numberStops, path: '/reports/stop' },

                    { label: t('speedViolation'), val: data?.speedLimited, unit: t('violations'), path: '/reports/event' },
                    { label: t('geofenceViolation'), val: data?.geofenceExited, unit: t('violations'), path: '/reports/event' },
                    { label: t('crossingNumber'), val: formatNumber(data?.crossingTotal, 0), unit: t('sharedGeofences'), path: '/reports/stop' },

                    { label: t('averageGPSSignal'), val: data?.gpsAverage?.toFixed(0), path: '/reports/chart', type: 'gpsSgn' },
                    { label: t('averageGSMSignal'), val: data?.gsmAverage?.toFixed(0), path: '/reports/chart', type: 'gsmSgn' },
                ].map((kpi, index) => (
                    <Grid item xs={6} sm={3} md={3} key={index} onClick={() => kpi.path && handleHistory(kpi.path, kpi.type)}>
                        <Paper className={classes.kpiCircleCard} elevation={0}>
                            <Box className={classes.kpiCircle}>
                                <Box className={classes.kpiValueContainer}>
                                    {loading ? <ValueLoader size={30} /> :
                                        <>
                                            <Typography className={classes.kpiValue}>{kpi.val ?? '0'}</Typography>
                                            <Typography className={classes.kpiUnit}>{kpi.unit ?? ''}</Typography>
                                        </>
                                    }
                                </Box>
                            </Box>
                            <Typography className={classes.kpiLabel}>{kpi.label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderQuickAccessReports = () => {
        const reports = [
            { label: t('reportRoutes') || 'Routes', icon: <TimelineIcon />, path: '/reports/route' },
            { label: t('reportEvents') || 'Events', icon: <PauseCircleFilledIcon />, path: '/reports/event' },
            { label: t('reportTrips') || 'Trips', icon: <PlayCircleFilledIcon />, path: '/reports/trip' },
            { label: t('reportStops') || 'Stops', icon: <NotificationsActiveIcon />, path: '/reports/stop' },
            { label: t('reportSummary') || 'Summary', icon: <FormatListBulletedIcon />, path: '/reports/summary' },
            { label: t('reportCharts') || 'Chart', icon: <TrendingUpIcon />, path: '/reports/chart' },
            { label: t('reportEmissions') || 'Emissions', icon: <EcoSharp />, path: '/reports/emissions' },
        ];

        return (
            <div className={classes.section}>
                <Typography variant="h5" className={classes.kpiSelectorTitle}>
                    {t('QuickAccessReports')}
                </Typography>
                <Grid container spacing={2}>
                    {reports.map((report, index) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                            <Paper className={classes.reportCard} onClick={() => history.push(report.path)} elevation={0}>
                                <div className={classes.reportIconContainer}>
                                    {React.cloneElement(report.icon, { className: classes.reportIcon })}
                                </div>
                                <Typography className={classes.reportLabel}>{report.label}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    };

    return (
        <div className={classes.settingRoot}>
            <CssBaseline />

            {!isSmallScreen && <div className={classes.appBarWrapper}>
                <MainToolbar/>
            </div>}

            <SettingsDrawer
                hideDrawer={hideDrawer}
                setHideDrawer={setHideDrawer}
                page={'dashboard'}
            />

            <main className={classes.content}>
                <div className={!isSmallScreen ? classes.toolbarSpacer : ''} />

                <Container maxWidth="lg" disableGutters>
                    <Box className={classes.pageHeader}>
                        <Typography variant="h4" className={classes.pageTitle} noWrap>
                            {t('dashboardTitle') || 'Dashboard'}
                        </Typography>
                    </Box>
                    {loading && isInitialLoad.current && (
                        <Box className={classes.fullPageLoader}>
                            <CircularProgress size={50} />
                        </Box>
                    )}
                    {(!loading || !isInitialLoad.current) && (
                        <>
                            {renderInfoCards()}
                            {renderKpiSelector()}
                            {renderKpiCircles()}
                            <Divider className={classes.dividerDashboard} />
                            {renderQuickAccessReports()}
                        </>
                    )}
                    {!loading && !isInitialLoad.current && (!data || Object.keys(data).length === 0) && (
                        <Typography variant="body1" color="textSecondary" align="center" style={{ margin: theme.spacing(4) }}>
                            {t('dashboardNoData')}
                        </Typography>
                    )}
                </Container>
            </main>
            <Snackbar open={!!openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(null)}>
                <Alert
                    onClose={() => setOpenSnackbar(null)}
                    variant="filled"
                    severity={'warning'}
                >
                    {t(openSnackbar)}
                </Alert>
            </Snackbar>
        </div>
    );
}


export default Dashboard;
